import React, { useState, useEffect } from 'react';
import { X, Archive, Search } from 'lucide-react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from "react-router-dom";
import dayjs from 'dayjs';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useNotifications } from '../contexts/NotificationContext';


const NotifyContainer = ({ open, onClose, singleNotification = null }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('all');
  const [archivingId, setArchivingId] = useState(null);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const navigate = useNavigate();
  const { getActiveNotifications, archiveNotification, loading } = useNotifications();
  
  // Get notifications from context or use singleNotification if provided
  const notifications = singleNotification ? [singleNotification] : getActiveNotifications();

  const handleClose = () => {
    if (singleNotification) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsAnimatingOut(false);
        onClose();
      }, 300); // Match animation duration
    } else {
      onClose();
    }
  };

  const handleArchive = async (notificationId) => {
    setArchivingId(notificationId);
    await archiveNotification(notificationId);
    setArchivingId(null);
  };

  // Filter out archived notifications (already done by getActiveNotifications, but keep for safety)
  const visibleNotifications = notifications.filter(n => !n.isArchived);

  const formatNotificationDate = (date) => {
    return dayjs(date).format('DD/MM/YYYY');
  };

  const getNotificationTitle = (notification) => {
    if (notification.type === 'deworming') {
      return t('DewormingReminder');
    }
    if (notification.type === 'nearbyLostPets') {
      return t('NearbyLostPets');
    }
    return 'Notification';
  };

  const getNotificationMessage = (notification) => {
    if (notification.type === 'deworming') {
      const nextDate = dayjs(notification.nextEventDate).format('DD/MM/YYYY');
      return `${notification.petName} - ${nextDate}`;
    }
    if (notification.type === 'nearbyLostPets' && notification.nearbyPetLost) {
      try {
        const url = new URL(notification.nearbyPetLost);
        return t('ClickIcon');
      } catch (e) {
        console.error('Error parsing nearbyPetLost URL:', e);
      }
    }
    return '';
  };

  if (!open) return null;

  const animationClass = singleNotification 
    ? (isAnimatingOut ? 'animate-slideUp' : 'animate-slideDown')
    : '';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40 "
        onClick={handleClose}
      />

      {/* Notification Panel */}
      <div className={`fixed left-1/2 -translate-x-1/2 top-[70px] w-[calc(100%-2rem)] md:left-auto md:right-40 md:translate-x-0 md:w-[450px] bg-white z-50 shadow-2xl overflow-y-auto border border-gray-200 rounded-lg max-h-[300px] ${animationClass}`}>

        {/* Content */}
        <div className="p-3 md:p-6">
          {/* Notifications List */}
          {loading ? (
            <div className="text-center py-12 text-gray-400">
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  {/* 載入中... */}
                  <img src="https://petpetclub.s3.ap-southeast-1.amazonaws.com/user-uploads/pets/68cb893a0feda12f2dbcc02b/68d4c8ce22baedfbe8b852dd.gif" alt=""
                    style={{
                      width: '100px',
                      height: 'auto',
                      mixBlendMode: 'multiply',
                    }} />
                  <div
                    style={{
                      fontWeight: "500px",
                      fontSize: "15px",
                      textAlign: "center",
                      fontFamily: "Lucida Console",

                    }}>
                    {t('Loading')}
                  </div>
                </Typography>
              </Box>
            </div>
          ) : visibleNotifications.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p>{t('NoNotificationsYet')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {visibleNotifications.map((notification, index) => (
                <div
                  key={notification._id || index}
                  className={`flex items-start gap-3 ${singleNotification ? 'pb-0' : 'pb-4'} border-b border-gray-100 last:border-0`}
                >
                  <div className="w-2 h-2 rounded-full bg-yellow-400 mt-2 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-base">
                          {getNotificationTitle(notification)}
                        </h4>
                        {notification.type === 'nearbyLostPets' && notification.nearbyPetLost && (
                          <Search
                            size={18}
                            className="text-yellow-500 hover:text-yellow-600 cursor-pointer"
                            onClick={() => {
                              onClose();
                              window.location.href = notification.nearbyPetLost;
                            }}
                          />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {formatNotificationDate(notification.createdAt)}
                        </span>
                        <button
                          onClick={() => handleArchive(notification._id)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors relative h-7"
                          title="Archive notification"
                          disabled={archivingId === notification._id}
                        >
                          {archivingId === notification._id ? (
                            <CircularProgress size={16} sx={{ color: '#9CA3AF',}} />
                          ) : (
                            <Archive size={16} className="text-gray-500 hover:text-gray-700 cursor-pointer" />
                          )}
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {getNotificationMessage(notification)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!singleNotification && (
            <div className="flex justify-end sticky z-10 right-0 bottom-0 bg-white">
              <button onClick={() => {
                onClose();
                navigate('/notification');
              }} className="text-sm text-gray-600 hover:text-gray-900 flex gap-1 cursor-pointer">
                {t('ViewAll')}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotifyContainer;
